import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, catchError, of, switchMap, timer } from 'rxjs';
import { SyncBacklog } from '../../../models/responses/return-sync';
import { backlogQueryParams } from '../../../modules/order-management/order-management-status.util';
import { PosSettingsService } from '../../../services/pos-settings.service';

export const SYNC_BACKLOG_POLL_MS = 7000;

/**
 * Footer badge with the orders + returns still waiting to sync. The count comes from the
 * middleware queue state, so it stays visible after reconnecting until everything is processed.
 * Each count links to the matching local tab (POS orders / returns) on the order page, pre-filtered by sync status.
 */
@Component({
  selector: 'app-sync-backlog-indicator',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule],
  template: `
    <ng-container *ngIf="backlog$ | async as backlog">
      <div
        *ngIf="backlog.total > 0 || backlog.rejected > 0"
        class="flex items-center gap-2 text-xs select-none"
        data-testid="sync-backlog-indicator"
      >
        <span
          *ngIf="backlog.ordersPending + backlog.returnsPending > 0"
          class="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-amber-800"
          [attr.title]="'sync_backlog.tooltip' | translate"
        >
          <i class="pi pi-sync"></i>
          {{ 'sync_backlog.waiting' | translate }}
          <a
            *ngIf="backlog.ordersPending > 0"
            class="underline"
            routerLink="/order-management"
            [queryParams]="params('pos', 'PENDING_SYNC')"
            data-testid="sync-backlog-orders-pending"
          >{{ 'sync_backlog.orders' | translate: { count: backlog.ordersPending } }}</a>
          <a
            *ngIf="backlog.returnsPending > 0"
            class="underline"
            routerLink="/order-management"
            [queryParams]="params('returns', 'PENDING_SYNC')"
            data-testid="sync-backlog-returns-pending"
          >{{ 'sync_backlog.returns' | translate: { count: backlog.returnsPending } }}</a>
        </span>
        <span
          *ngIf="backlog.failed > 0"
          class="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-orange-800"
          [attr.title]="'sync_backlog.tooltip' | translate"
        >
          <i class="pi pi-times-circle"></i>
          {{ 'sync_backlog.failed' | translate }}
          <a
            *ngIf="backlog.ordersFailed > 0"
            class="underline"
            routerLink="/order-management"
            [queryParams]="params('pos', 'SYNC_FAILED')"
            data-testid="sync-backlog-orders-failed"
          >{{ 'sync_backlog.orders' | translate: { count: backlog.ordersFailed } }}</a>
          <a
            *ngIf="backlog.returnsFailed > 0"
            class="underline"
            routerLink="/order-management"
            [queryParams]="params('returns', 'SYNC_FAILED')"
            data-testid="sync-backlog-returns-failed"
          >{{ 'sync_backlog.returns' | translate: { count: backlog.returnsFailed } }}</a>
        </span>
        <span
          *ngIf="backlog.rejected > 0"
          class="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-red-800"
        >
          <i class="pi pi-exclamation-triangle"></i>
          {{ 'sync_backlog.rejected' | translate }}
          <a
            *ngIf="backlog.ordersRejected > 0"
            class="underline"
            routerLink="/order-management"
            [queryParams]="params('pos', 'SYNC_REJECTED')"
            data-testid="sync-backlog-orders-rejected"
          >{{ 'sync_backlog.orders' | translate: { count: backlog.ordersRejected } }}</a>
          <a
            *ngIf="backlog.returnsRejected > 0"
            class="underline"
            routerLink="/order-management"
            [queryParams]="params('returns', 'SYNC_REJECTED')"
            data-testid="sync-backlog-returns-rejected"
          >{{ 'sync_backlog.returns' | translate: { count: backlog.returnsRejected } }}</a>
        </span>
      </div>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncBacklogIndicatorComponent {
  private readonly posSettings = inject(PosSettingsService);

  readonly params = backlogQueryParams;

  readonly backlog$: Observable<SyncBacklog | null> = timer(0, SYNC_BACKLOG_POLL_MS).pipe(
    switchMap(() => this.posSettings.getSyncBacklog().pipe(catchError(() => of(null)))),
  );
}
